import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  Button,
  LoadingSpinner,
} from "@/components/ui";
import type { MatterSurvey } from "@/lib/data/requests/matters/matter-survey";
import type { APIError } from "@/lib/types/APIResponse";
import { isAxiosError } from "axios";
import { useState } from "react";

export type MatterSurveyAction = {
  kind: "cancel" | "delete";
  survey: MatterSurvey;
};

interface MatterSurveyActionDialogProps {
  action: MatterSurveyAction | null;
  onClose: () => void;
  onConfirm: (action: MatterSurveyAction) => Promise<void>;
}

export function MatterSurveyActionDialog({
  action,
  onClose,
  onConfirm,
}: MatterSurveyActionDialogProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const isDelete = action?.kind === "delete";

  const close = () => {
    if (pending) return;
    setError("");
    onClose();
  };

  const confirm = async () => {
    if (!action || pending) return;
    setPending(true);
    setError("");
    try {
      await onConfirm(action);
      onClose();
    } catch (cause) {
      const fallback =
        action.kind === "cancel"
          ? "The survey could not be canceled. Please try again."
          : "The survey could not be deleted. Please try again.";
      const response = isAxiosError(cause)
        ? (cause.response?.data as Partial<APIError> | undefined)
        : undefined;
      setError(response?.meta?.userMsg ?? fallback);
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog
      open={Boolean(action)}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <AlertDialogContent className="border-border bg-card text-card-foreground shadow-lg">
        <AlertDialogTitle>
          {isDelete ? "Delete survey?" : "Cancel survey?"}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {isDelete
            ? `“${action.survey.title}”, its questions, answers, and survey recipient link will be permanently deleted. This cannot be undone.`
            : `“${action?.survey.title}” will no longer accept responses. The survey and its history will remain available.`}
        </AlertDialogDescription>
        {error ? (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <AlertDialogCancel asChild>
            <Button variant="outline" disabled={pending} onClick={close}>
              Keep survey
            </Button>
          </AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={pending}
            onClick={() => void confirm()}
          >
            {pending ? <LoadingSpinner className="size-4" /> : null}
            {isDelete ? "Delete" : "Cancel survey"}
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
